import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    Camera,
    CheckCircle2,
    ClipboardCheck,
    ExternalLink,
    History,
    Loader2,
    MessageSquarePlus,
    Pencil,
    Play,
    Send,
    ShieldCheck,
    Trash2,
    XCircle,
} from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RoleGate } from "@/components/auth/RoleGate";
import { WoStatusBadge } from "@/components/common/WoStatusBadge";
import { FinishOrSendToWorkshopDialog } from "@/features/workshop/components/FinishOrSendToWorkshopDialog";
import { workOrdersService } from "@/services/work_orders.service";
import { customersService } from "@/services/customers.service";
import { techniciansService } from "@/services/technicians.service";
import { uploadsService } from "@/services/uploads.service";
import { getErrorMessage } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import {
    OS_PRIORITY_LABELS,
} from "@/config/constants";
import { formatDateTime, mediaUrl } from "@/lib/utils";

function Info({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-sm text-foreground">{value ?? "—"}</div>
        </div>
    );
}

const EVENT_LABELS = {
    created: "OS criada",
    sent: "Enviada ao técnico",
    accepted: "OS aceita",
    auto_accepted: "OS aceita automaticamente (criada pelo técnico)",
    rejected: "OS recusada pelo técnico",
    started: "Atendimento iniciado",
    note_added: "Observação registrada",
    photo_added: "Foto adicionada",
    finished: "OS finalizada",
    cancelled: "OS cancelada",
    migrated_from_service_call: "Migrada de chamado legado",
};

/** Sanitize phone number to E.164-ish digits for wa.me deep link. */
function toWaDigits(phone) {
    if (!phone) return "";
    const digits = String(phone).replace(/\D/g, "");
    // Assume Brazilian numbers if 10-11 digits without country code
    if (digits.length === 10 || digits.length === 11) return `55${digits}`;
    return digits;
}

function buildWhatsappUrl(phone, message) {
    const num = toWaDigits(phone);
    const encoded = encodeURIComponent(message);
    return num ? `https://wa.me/${num}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
}

export default function WorkOrderDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { user } = useAuth();
    const isAdmin = user?.role === "Administrator";
    const isTechnician = user?.role === "Technician";

    const [noteOpen, setNoteOpen] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [photoOpen, setPhotoOpen] = useState(false);
    const [photoCaption, setPhotoCaption] = useState("");
    const [uploading, setUploading] = useState(false);
    const [finishDialogOpen, setFinishDialogOpen] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");

    const detail = useQuery({ queryKey: ["work_order", id], queryFn: () => workOrdersService.get(id) });
    const wo = detail.data;
    const customer = useQuery({
        queryKey: ["customer", wo?.customer_id],
        queryFn: () => customersService.get(wo.customer_id),
        enabled: !!wo?.customer_id,
    });
    // Fetch technicians (only when admin needs the phone/name for WhatsApp)
    const technicians = useQuery({
        queryKey: ["technicians-for-wo", id],
        queryFn: () => techniciansService.list({ pageSize: 200 }),
        enabled: isAdmin && !!wo,
    });

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["work_order", id] });
        qc.invalidateQueries({ queryKey: ["work_orders"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
        qc.invalidateQueries({ queryKey: ["asset-timeline"] });
    };

    const accept = useMutation({
        mutationFn: () => workOrdersService.accept(id),
        onSuccess: () => { toast.success("OS aceita."); invalidate(); },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao aceitar OS.")),
    });
    const send = useMutation({
        mutationFn: () => workOrdersService.send(id),
        onSuccess: () => {
            toast.success("OS enviada ao técnico.");
            invalidate();
            setSendDialogOpen(false);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao enviar OS.")),
    });
    const reject = useMutation({
        mutationFn: () => workOrdersService.reject(id, rejectReason.trim()),
        onSuccess: () => {
            toast.success("OS recusada.");
            setRejectReason("");
            setRejectDialogOpen(false);
            invalidate();
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao recusar OS.")),
    });
    const start = useMutation({
        mutationFn: () => workOrdersService.start(id),
        onSuccess: () => { toast.success("Atendimento iniciado."); invalidate(); },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao iniciar.")),
    });
    const finish = useMutation({
        mutationFn: () => workOrdersService.finish(id),
        onSuccess: () => { toast.success("OS finalizada."); invalidate(); },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao finalizar.")),
    });
    const cancel = useMutation({
        mutationFn: () => workOrdersService.cancel(id, "Cancelada pelo administrador"),
        onSuccess: () => { toast.success("OS cancelada."); invalidate(); },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao cancelar.")),
    });

    const [deleteOpen, setDeleteOpen] = useState(false);
    const removeMut = useMutation({
        mutationFn: () => workOrdersService.remove(id),
        onSuccess: () => {
            toast.success("Ordem de serviço excluída.");
            qc.invalidateQueries({ queryKey: ["work_orders"] });
            navigate("/work-orders", { replace: true });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao excluir OS.")),
    });

    const submitNote = useMutation({
        mutationFn: () => workOrdersService.addNote(id, noteText.trim()),
        onSuccess: () => {
            toast.success("Observação registrada.");
            setNoteText("");
            setNoteOpen(false);
            invalidate();
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao registrar observação.")),
    });

    async function submitPhoto(file) {
        if (!file) return;
        setUploading(true);
        try {
            const uploaded = await uploadsService.upload(file);
            await workOrdersService.addPhoto(id, {
                url: uploaded.url,
                caption: photoCaption.trim() || null,
            });
            toast.success("Foto adicionada.");
            setPhotoCaption("");
            setPhotoOpen(false);
            invalidate();
        } catch (e) {
            toast.error(getErrorMessage(e, "Falha ao adicionar foto."));
        } finally {
            setUploading(false);
        }
    }

    if (detail.isLoading || !wo) {
        return <div className="space-y-6"><PageHeader title="OS" backTo="/work-orders" /><Skeleton className="h-64 w-full" /></div>;
    }

    const canSend = isAdmin && wo.status === "nova" && (wo.technician_ids || []).length > 0;
    const canAcceptFromSent = isTechnician && wo.status === "enviada";
    const canRejectFromSent = isTechnician && wo.status === "enviada";
    const canAdminAcceptFromNova = isAdmin && wo.status === "nova";
    const canStart = wo.status === "aceita";
    const canFinish = wo.status === "em_atendimento";
    const canAddNotePhoto = wo.status === "em_atendimento";
    const canCancel = isAdmin && !["finalizada", "cancelada"].includes(wo.status);
    const canEdit = isAdmin && ["nova", "aceita"].includes(wo.status);

    // Assigned technician display and WhatsApp deep link (for Admin send action)
    const assignedTechnicianIds = wo.technician_ids || [];
    const allTechs = technicians.data?.items || [];
    const assignedTech = allTechs.find((t) => assignedTechnicianIds.includes(t.id));
    const woUrl = `${window.location.origin}/work-orders/${id}`;
    const waMessage = assignedTech
        ? (
            `Olá, ${assignedTech.name}!` +
            `\n\nNova ordem de serviço da Prosper360 para você:` +
            `\n\n📋 OS: ${wo.number}` +
            `\n📌 Título: ${wo.title}` +
            (customer.data?.legal_name ? `\n👤 Cliente: ${customer.data.legal_name}` : "") +
            (wo.priority ? `\n⚡ Prioridade: ${OS_PRIORITY_LABELS[wo.priority] || wo.priority}` : "") +
            `\n\nAcesse o sistema para aceitar ou recusar:\n${woUrl}`
        )
        : `Nova OS ${wo.number} disponível: ${woUrl}`;
    const whatsappUrl = buildWhatsappUrl(assignedTech?.phone, waMessage);

    return (
        <div className="space-y-6">
            <PageHeader
                title={wo.number}
                description={wo.title}
                backTo="/work-orders"
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "OS", to: "/work-orders" },
                    { label: wo.number },
                ]}
                actions={
                    <div className="flex flex-wrap gap-2">
                        {canEdit && (
                            <RoleGate resource="work_orders" action="update">
                                <Button
                                    variant="outline"
                                    onClick={() => navigate(`/work-orders/${id}/edit`)}
                                    data-testid="wo-edit-button"
                                >
                                    <Pencil className="mr-2 h-4 w-4" /> Editar
                                </Button>
                            </RoleGate>
                        )}
                        {canSend && (
                            <RoleGate resource="work_orders" action="update">
                                <Button
                                    onClick={() => setSendDialogOpen(true)}
                                    className="bg-secondary text-secondary-foreground hover:opacity-90"
                                    data-testid="wo-send-button"
                                >
                                    <Send className="mr-2 h-4 w-4" />
                                    Enviar para técnico
                                </Button>
                            </RoleGate>
                        )}
                        {canAcceptFromSent && (
                            <RoleGate resource="work_orders" action="update">
                                <Button
                                    onClick={() => accept.mutate()}
                                    disabled={accept.isPending}
                                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                                    data-testid="wo-accept-sent-button"
                                >
                                    {accept.isPending ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <CheckCircle2 className="mr-2 h-4 w-4" />
                                    )}
                                    Aceitar atendimento
                                </Button>
                            </RoleGate>
                        )}
                        {canRejectFromSent && (
                            <RoleGate resource="work_orders" action="update">
                                <Button
                                    variant="outline"
                                    className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/50 dark:text-red-300 dark:hover:bg-red-500/10"
                                    onClick={() => setRejectDialogOpen(true)}
                                    data-testid="wo-reject-button"
                                >
                                    <XCircle className="mr-2 h-4 w-4" /> Recusar
                                </Button>
                            </RoleGate>
                        )}
                        {canAdminAcceptFromNova && (
                            <RoleGate resource="work_orders" action="update">
                                <Button
                                    variant="outline"
                                    onClick={() => accept.mutate()}
                                    disabled={accept.isPending}
                                    data-testid="wo-accept-button"
                                >
                                    {accept.isPending ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <ShieldCheck className="mr-2 h-4 w-4" />
                                    )}
                                    Marcar como aceita
                                </Button>
                            </RoleGate>
                        )}
                        {canStart && (
                            <RoleGate resource="work_orders" action="update">
                                <Button
                                    onClick={() => start.mutate()}
                                    disabled={start.isPending}
                                    className="bg-secondary text-secondary-foreground hover:opacity-90"
                                    data-testid="wo-start-button"
                                >
                                    {start.isPending ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Iniciar Atendimento
                                </Button>
                            </RoleGate>
                        )}
                        {canAddNotePhoto && (
                            <RoleGate resource="work_orders" action="update">
                                <Button
                                    variant="outline"
                                    onClick={() => setNoteOpen(true)}
                                    data-testid="wo-add-note-button"
                                >
                                    <MessageSquarePlus className="mr-2 h-4 w-4" />
                                    Observação
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setPhotoOpen(true)}
                                    data-testid="wo-add-photo-button"
                                >
                                    <Camera className="mr-2 h-4 w-4" />
                                    Foto
                                </Button>
                            </RoleGate>
                        )}
                        {canFinish && (
                            <RoleGate resource="work_orders" action="update">
                                <Button
                                    className="bg-brand-navy text-primary-foreground hover:bg-brand-navy/90"
                                    onClick={() => setFinishDialogOpen(true)}
                                    data-testid="wo-finish-button"
                                >
                                    <ClipboardCheck className="mr-2 h-4 w-4" />
                                    Finalizar OS
                                </Button>
                            </RoleGate>
                        )}
                        {canCancel && (
                            <Button
                                variant="ghost"
                                className="text-danger hover:text-danger"
                                onClick={() => cancel.mutate()}
                                disabled={cancel.isPending}
                                data-testid="wo-cancel-button"
                            >
                                <XCircle className="mr-2 h-4 w-4" /> Cancelar
                            </Button>
                        )}
                        {isAdmin && (
                            <RoleGate resource="work_orders" action="delete">
                                <Button
                                    variant="outline"
                                    className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/50 dark:text-red-300 dark:hover:bg-red-500/10"
                                    onClick={() => setDeleteOpen(true)}
                                    data-testid="wo-delete-button"
                                >
                                    <Trash2 className="mr-2 h-4 w-4" /> Excluir
                                </Button>
                            </RoleGate>
                        )}
                    </div>
                }
            />

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-semibold">Dados da OS</CardTitle>
                            <WoStatusBadge value={wo.status} size="lg" withDot />
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-3">
                            <Info label="Número" value={<span className="font-mono">{wo.number}</span>} />
                            <Info label="Data de abertura" value={formatDateTime(wo.created_at)} />
                            <Info label="Prioridade" value={OS_PRIORITY_LABELS[wo.priority] || wo.priority} />
                            <Info label="Cliente" value={customer.data?.legal_name} />
                            <Info label="Contato do cliente" value={customer.data?.phone || customer.data?.email || "—"} />
                            <Info label="Técnicos designados" value={`${(wo.technician_ids || []).length}`} />
                            <Info label="Categoria" value={wo.category} />
                            <Info label="Aceita em" value={wo.accepted_at ? formatDateTime(wo.accepted_at) : "—"} />
                            <Info label="Início do atendimento" value={wo.actual_start ? formatDateTime(wo.actual_start) : "—"} />
                            <Info label="Finalizada em" value={wo.actual_end ? formatDateTime(wo.actual_end) : "—"} />
                        </CardContent>
                    </Card>

                    {wo.description && (
                        <Card className="rounded-xl border shadow-sm">
                            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Descrição da solicitação</CardTitle></CardHeader>
                            <CardContent className="whitespace-pre-wrap text-sm">{wo.description}</CardContent>
                        </Card>
                    )}

                    <Card className="rounded-xl border shadow-sm" data-testid="wo-notes-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">
                                Observações do atendimento ({(wo.notes || []).length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {(wo.notes || []).length === 0 ? (
                                <div className="rounded-lg border border-dashed bg-muted/40 px-4 py-6 text-center text-xs text-muted-foreground">
                                    Nenhuma observação registrada ainda.
                                </div>
                            ) : (
                                <ol className="space-y-3">
                                    {[...wo.notes].reverse().map((n, i) => (
                                        <li
                                            key={`wo-note-${n.at || "no-date"}-${i}`}
                                            className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm"
                                            data-testid={`wo-note-item`}
                                        >
                                            <div className="whitespace-pre-wrap text-foreground/90">{n.text}</div>
                                            <div className="mt-1 text-[11px] text-muted-foreground">
                                                {formatDateTime(n.at)}
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="rounded-xl border shadow-sm" data-testid="wo-photos-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">
                                Fotos do atendimento ({(wo.photos || []).length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {(wo.photos || []).length === 0 ? (
                                <div className="rounded-lg border border-dashed bg-muted/40 px-4 py-6 text-center text-xs text-muted-foreground">
                                    Nenhuma foto adicionada ainda.
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                                    {(wo.photos || []).map((p, i) => (
                                        <a
                                            key={`wo-photo-${p.url || i}`}
                                            href={mediaUrl(p.url)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group overflow-hidden rounded-xl border"
                                            data-testid="wo-photo-item"
                                        >
                                            <img
                                                src={mediaUrl(p.url)}
                                                alt={p.caption || "Foto do atendimento"}
                                                className="h-32 w-full object-cover transition group-hover:scale-105"
                                            />
                                            {p.caption && (
                                                <div className="px-2 py-1 text-[11px] text-muted-foreground line-clamp-1">
                                                    {p.caption}
                                                </div>
                                            )}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2 flex flex-row items-center gap-2">
                            <History className="h-4 w-4 text-muted-foreground" />
                            <CardTitle className="text-sm font-semibold">Histórico</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {(wo.events || []).length === 0 ? (
                                <div className="text-xs text-muted-foreground">Sem eventos.</div>
                            ) : (
                                <ol className="relative space-y-4 border-l pl-5">
                                    {[...wo.events].reverse().map((e, i) => (
                                        <li key={`wo-event-${e.at || "no-date"}-${e.action || "action"}-${i}`} className="relative">
                                            <span className="absolute -left-[9px] top-1 h-2.5 w-2.5 rounded-full bg-secondary" />
                                            <div className="text-xs font-medium">
                                                {EVENT_LABELS[e.action] || e.action}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground">{formatDateTime(e.at)}</div>
                                            {e.note && (
                                                <div className="text-xs text-muted-foreground line-clamp-2">{e.note}</div>
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Note dialog */}
            <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
                <DialogContent className="max-w-lg" data-testid="wo-note-dialog">
                    <DialogHeader>
                        <DialogTitle>Registrar observação</DialogTitle>
                        <DialogDescription>
                            A observação ficará registrada no histórico da OS.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-1.5 pt-2">
                        <Label>Observação *</Label>
                        <Textarea
                            rows={5}
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Ex.: encontrado rolamento com desgaste, aguardando peça…"
                            data-testid="wo-note-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setNoteOpen(false)}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={() => submitNote.mutate()}
                            disabled={submitNote.isPending || !noteText.trim()}
                            className="bg-brand-navy text-primary-foreground hover:bg-brand-navy/90"
                            data-testid="wo-note-submit"
                        >
                            {submitNote.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                            )}
                            Registrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Photo dialog */}
            <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
                <DialogContent className="max-w-lg" data-testid="wo-photo-dialog">
                    <DialogHeader>
                        <DialogTitle>Adicionar foto</DialogTitle>
                        <DialogDescription>
                            Envie uma foto do atendimento. Data, hora e autor são registrados automaticamente.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 pt-2">
                        <div className="space-y-1.5">
                            <Label>Legenda (opcional)</Label>
                            <Input
                                value={photoCaption}
                                onChange={(e) => setPhotoCaption(e.target.value)}
                                placeholder="Ex.: placa do motor, dano visível…"
                                data-testid="wo-photo-caption"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Arquivo *</Label>
                            <Input
                                type="file"
                                accept="image/*"
                                disabled={uploading}
                                onChange={(e) => submitPhoto(e.target.files?.[0])}
                                data-testid="wo-photo-file"
                            />
                            <p className="text-[11px] text-muted-foreground">
                                Formatos: JPG, PNG. Máx 10MB.
                            </p>
                        </div>
                        {uploading && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                <span>Enviando foto…</span>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setPhotoOpen(false)} disabled={uploading}>
                            Fechar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Finalizar em campo OU enviar para oficina (Módulo 04) */}
            <FinishOrSendToWorkshopDialog
                open={finishDialogOpen}
                onOpenChange={setFinishDialogOpen}
                workOrderId={id}
                workOrderNumber={wo?.number}
            />

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Excluir ordem de serviço?</AlertDialogTitle>
                        <AlertDialogDescription>
                            A OS <span className="font-mono font-medium">{wo?.number}</span> será marcada como excluída. Se ela já foi enviada para oficina ou tem propostas comerciais vinculadas, a operação poderá ser bloqueada pelo servidor.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="wo-delete-cancel">Manter</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); removeMut.mutate(); }}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="wo-delete-confirm"
                        >
                            Excluir OS
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Send to technician dialog (WhatsApp deep link) */}
            <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
                <DialogContent className="max-w-lg" data-testid="wo-send-dialog">
                    <DialogHeader>
                        <DialogTitle>Enviar OS ao técnico</DialogTitle>
                        <DialogDescription>
                            A OS será marcada como <b>Aguardando aceite</b> e o técnico receberá o link
                            {assignedTech ? ` via WhatsApp (${assignedTech.name})` : " via WhatsApp"}.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 pt-2 text-sm">
                        <div className="rounded-md border bg-muted/30 p-3">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                Técnico designado
                            </div>
                            <div className="mt-1 font-medium">
                                {assignedTech?.name || "Nenhum técnico com telefone cadastrado"}
                            </div>
                            {assignedTech?.phone ? (
                                <div className="text-xs text-muted-foreground">{assignedTech.phone}</div>
                            ) : (
                                <div className="text-xs text-amber-700">
                                    Cadastre o telefone do técnico para enviar automaticamente.
                                </div>
                            )}
                        </div>
                        <div className="rounded-md border p-3">
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                Mensagem que será enviada
                            </div>
                            <pre className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/80">{waMessage}</pre>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="ghost"
                            onClick={() => setSendDialogOpen(false)}
                            disabled={send.isPending}
                            data-testid="wo-send-cancel"
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                send.mutate();
                                window.open(whatsappUrl, "_blank", "noopener,noreferrer");
                            }}
                            disabled={send.isPending}
                            className="border-emerald-500/40 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300"
                            data-testid="wo-send-whatsapp"
                        >
                            {send.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <ExternalLink className="mr-2 h-4 w-4" />
                            )}
                            Marcar como enviada e abrir WhatsApp
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject dialog */}
            <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                <DialogContent data-testid="wo-reject-dialog">
                    <DialogHeader>
                        <DialogTitle>Recusar atendimento</DialogTitle>
                        <DialogDescription>
                            Informe o motivo. A OS voltará para status <b>Nova</b> e o administrador poderá
                            reatribuir a outro técnico.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 pt-2">
                        <Label htmlFor="reject-reason">Motivo *</Label>
                        <Textarea
                            id="reject-reason"
                            rows={4}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Ex.: Sem disponibilidade nesta semana, indisponibilidade de peça, etc."
                            data-testid="wo-reject-reason"
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="ghost"
                            onClick={() => setRejectDialogOpen(false)}
                            disabled={reject.isPending}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => reject.mutate()}
                            disabled={reject.isPending || rejectReason.trim().length < 3}
                            data-testid="wo-reject-confirm"
                        >
                            {reject.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <XCircle className="mr-2 h-4 w-4" />
                            )}
                            Recusar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
